import React, { useState, useEffect, useRef } from 'react';
import { useNetworkStability } from '../../../../app/src/hooks/useNetworkStability';
import Icon from '../Icon';

const severityStyles = {
  error: {
    color: '#5f2120',
    background: '#fdeded',
    border: '#f5c2c0',
    iconName: 'notifications-error',
  },
  warning: {
    color: '#663c00',
    background: '#fff4e5',
    border: 'transparent',
    iconName: null,
  },
  success: {
    color: '#1e4620',
    background: '#edf7ed',
    border: '#a5d6a7',
    iconName: 'notifications-success',
  },
  info: {
    color: '#014361',
    background: '#e5f6fd',
    border: '#81d4fa',
    iconName: 'notifications-info',
  },
};

export function AlertConnection() {
  const network = useNetworkStability();
  const [open, setOpen] = useState(false);
  const [ui, setUi] = useState({
    title: '',
    message: '',
    severity: 'info' as 'error' | 'warning' | 'success' | 'info',
  });

  const wasOffline = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const severity = severityStyles[ui.severity] || severityStyles.info;

  useEffect(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (!network.isOnline) {
      setUi({
        title: 'No connection',
        message: 'Please check your internet connection.',
        severity: 'error',
      });
      setOpen(true);
      wasOffline.current = true;
    } else if (!network.isApiReachable) {
      setUi({
        title: 'Server unavailable',
        message: 'There is an issue with the server connection. Retrying...',
        severity: 'error',
      });
      setOpen(true);
      wasOffline.current = true;
    } else if (network.stability === 'unstable') {
      setUi({
        title: 'Slow connection',
        message: 'Connection slow. Rechecking ...',
        severity: 'warning',
      });
      setOpen(true);
    } else {
      if (wasOffline.current) {
        setUi({
          title: 'System online',
          message: 'Connection successfully restored.',
          severity: 'success',
        });
        setOpen(true);
        wasOffline.current = false;
        closeTimeoutRef.current = setTimeout(() => {
          setOpen(false);
          closeTimeoutRef.current = null;
        }, 5000);
      } else {
        setOpen(false);
      }
    }

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [network]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: '90%',
        maxWidth: 400,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          maxHeight: open ? 140 : 0,
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0px)' : 'translateY(-8px)',
          overflow: 'hidden',
          transition: 'max-height 220ms ease, opacity 180ms ease, transform 220ms ease',
        }}
      >
        <div
          role="alert"
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '10px 14px',
            borderRadius: 4,
            border: `1px solid ${severity.border}`,
            backgroundColor: severity.background,
            color: severity.color,
            boxShadow: '0px 2px 12px rgba(0,0,0,0.12)',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              width: 22,
              height: 22,
              minWidth: 22,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 1,
            }}
          >
            {ui.severity === 'warning' ? (
              <div
                aria-hidden="true"
                style={{
                  position: 'relative',
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '18px solid #ed6c02',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: -2,
                    top: 5,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  !
                </span>
              </div>
            ) : (
              <Icon
                name={severity.iconName}
                className="h-5 w-5"
              />
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.4 }}>{ui.title}</div>
            <div style={{ fontWeight: 400, fontSize: 14, lineHeight: 1.45 }}>{ui.message}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AlertConnection;
